import { useEffect } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import type { UserRole } from "../shared/contracts";
import { useApp } from "./context/AppContext";
import { AppShell } from "./components/AppShell";
import { AdminPage } from "./pages/AdminPage";
import { AuthPage } from "./pages/AuthPage";
import { GroupChatPage } from "./pages/GroupChatPage";
import { GroupsPage } from "./pages/GroupsPage";
import { LegalPage } from "./pages/LegalPage";
import { MallPortalPage } from "./pages/MallPortalPage";
import { MessagesPage } from "./pages/MessagesPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { PublishPage } from "./pages/PublishPage";
import { PostDetailPage } from "./pages/PostDetailPage";
import { SellerPage } from "./pages/SellerPage";
import { SquarePage } from "./pages/SquarePage";
import { WarehousePage } from "./pages/WarehousePage";
import { InstallPage, SetupLoadingPage, SetupUnavailablePage } from "./pages/InstallPage";
import { AccountPage } from "./pages/AccountPage";
import { SharedPostPage } from "./pages/SharedPostPage";
import { SharedProfilePage } from "./pages/SharedProfilePage";
import { SharedCategoryPage } from "./pages/SharedCategoryPage";

function Guard({ roles }: { roles?: UserRole[] }) {
  const { user, authLoading } = useApp();
  const location = useLocation();
  if (authLoading) return <div className="route-loading" aria-label="正在检查登录状态"><span /></div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  if (user.onboardingRequired && location.pathname !== "/account") {
    return <Navigate to="/account?setup=email" replace />;
  }
  if (roles && !roles.includes(user.role)) return <Navigate to="/square" replace />;
  return <Outlet />;
}

function RouteScrollReset() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  return null;
}

export function App() {
  const { setupStatus, setupLoading, setupError, refreshSetup } = useApp();

  if (setupLoading) {
    return <SetupLoadingPage />;
  }
  if (setupError || !setupStatus) {
    return <SetupUnavailablePage message={setupError} onRetry={() => void refreshSetup()} />;
  }
  if (!setupStatus.installed) {
    return (
      <Routes>
        <Route path="/install" element={<InstallPage />} />
        <Route path="*" element={<Navigate to="/install" replace />} />
      </Routes>
    );
  }

  return (
    <><RouteScrollReset /><Routes>
      <Route path="install" element={<Navigate to="/square" replace />} />
      <Route path="share/posts/:shareToken" element={<SharedPostPage />} />
      <Route path="share/profiles/:shareToken" element={<SharedProfilePage />} />
      <Route path="share/categories/:shareToken" element={<SharedCategoryPage />} />
      <Route element={<AppShell />}>
        <Route index element={<Navigate to="/square" replace />} />
        <Route path="square" element={<SquarePage />} />
        <Route path="posts/:postId" element={<PostDetailPage />} />
        <Route path="seller/:userId" element={<SellerPage />} />
        <Route path="groups" element={<GroupsPage />} />
        <Route path="login" element={<AuthPage mode="login" />} />
        <Route path="register" element={<AuthPage mode="register" />} />
        <Route path="mall-portal" element={<MallPortalPage />} />
        <Route path="legal/:document" element={<LegalPage />} />
        <Route element={<Guard />}>
          <Route path="messages" element={<MessagesPage />} />
          <Route path="messages/:sessionId" element={<MessagesPage />} />
          <Route path="groups/:groupId" element={<GroupChatPage />} />
          <Route path="account" element={<AccountPage />} />
          <Route path="post/new" element={<PublishPage />} />
          <Route path="posts/:postId/edit" element={<PublishPage />} />
          <Route path="warehouse" element={<WarehousePage />} />
        </Route>
      </Route>
      <Route element={<Guard roles={["admin"]} />}>
        <Route path="admin" element={<AdminPage />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes></>
  );
}
